import React, { useEffect, useState } from "react";
import {
  Users2,
  Briefcase,
  Trophy,
  Building2,
  ShieldCheck,
  ClipboardCheck,
  Network,
  Sparkles,
} from "lucide-react";
import { PageHeader } from "../layout/Shell";
import { Badge } from "../ui/badge";
import { Kpi, Panel, ItemList, PageLink } from "./widgets";
import { api } from "../../lib/api";

/**
 * Engineering College dashboard variant.
 * Optimised for the campus-placement-driven Indian engineering college persona:
 * placement-cell metrics are first-class citizens, branches replace
 * "programmes", and the Training & Placement Cell gets a dedicated panel.
 *
 * Triggered by `inst.type === "Engineering College"`.
 */
export function EngineeringCollegeDashboard({ inst, m, user }) {
  const [counts, setCounts] = useState({});
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    if (!inst) return;
    Promise.all([
      api.get(`/dashboard/${inst.id}`).then((r) => r.data?.counts || {}),
      api.get(`/academic/${inst.id}/departments`).then((r) => r.data || []),
    ])
      .then(([c, d]) => {
        setCounts(c);
        setDepartments(d);
      })
      .catch(() => {});
  }, [inst?.id]);

  const currency = inst.country === "India" ? "₹" : "US$";
  const highest = m.highest_package_lpa
    ? `${currency}${m.highest_package_lpa} LPA`
    : "—";
  const avg = m.average_package_lpa ? `${currency}${m.average_package_lpa} LPA` : "—";
  const employers = m.companies_recruiting ?? 0;
  const alumni = m.alumni_network ?? 0;
  const placement = m.placement_rate ?? null;

  return (
    <div data-testid="dashboard-engineering-college">
      <PageHeader
        eyebrow={`${inst.type} · Placement-first Cockpit`}
        title={`${inst.short_name} · Principal's View`}
        description={
          inst.description ||
          `Affiliated to ${inst.compliance_framework || "your university"}.`
        }
        actions={
          <>
            {placement != null && (
              <Badge variant="outline" className="gap-1.5">
                <Trophy className="h-3 w-3" />
                {placement}% placed
              </Badge>
            )}
            <Badge variant="outline" className="gap-1.5">
              <Sparkles className="h-3 w-3" /> {(m.ai_sessions ?? 0).toLocaleString()} AI sessions
            </Badge>
            <Badge className="bg-primary text-primary-foreground">
              {user?.role === "institution_admin" ? "Principal" : "Engineering College"}
            </Badge>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-8">
        {/* Hero KPI strip — placement is the headline */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            label="Placement rate"
            value={placement != null ? `${placement}%` : "—"}
            hint={alumni ? `${alumni.toLocaleString()}+ alumni network` : undefined}
            icon={Briefcase}
            testid="eng-kpi-placement"
          />
          <Kpi
            label="Highest package"
            value={highest}
            hint={avg !== "—" ? `Average ${avg}` : undefined}
            icon={Trophy}
            testid="eng-kpi-highest"
          />
          <Kpi
            label="Recruiting companies"
            value={employers ? `${employers}+` : "—"}
            hint="Live cycle"
            icon={Building2}
            testid="eng-kpi-employers"
          />
          <Kpi
            label="Active students"
            value={m.students?.toLocaleString() ?? "—"}
            hint={`${m.faculty ?? "—"} faculty`}
            icon={Users2}
            testid="eng-kpi-students"
          />
        </section>

        {/* Branches + Placement highlights */}
        <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <Panel
            eyebrow="Branches"
            title="Department snapshot"
            testid="eng-departments"
            className="lg:col-span-3"
            action={<PageLink to="/academic-structure" label="Open structure" />}
          >
            {departments.length === 0 && (
              <div className="py-6 text-center text-xs text-muted-foreground">
                No departments seeded yet.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {departments.slice(0, 10).map((d) => (
                <div
                  key={d.id}
                  className="flex items-start justify-between gap-3 border-b border-border last:border-0 pb-2 last:pb-0"
                  data-testid={`eng-dept-${d.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate">{d.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {d.head || "Head TBD"}
                    </div>
                  </div>
                  <Network className="h-3.5 w-3.5 text-muted-foreground mt-1 shrink-0" />
                </div>
              ))}
            </div>
          </Panel>

          <Panel
            eyebrow="Headline offers"
            title="Top placements · live cycle"
            testid="eng-offers"
            className="lg:col-span-2"
            action={<PageLink to="/" label="Career Services" />}
          >
            <ItemList
              testid="eng-offers-list"
              items={[]}
              empty="No placement offers data available."
            />
          </Panel>
        </section>

        {/* Recruiter relationships */}
        <Panel
          eyebrow="Recruiter relationships"
          title="Companies recruiting this cycle"
          testid="eng-recruiters"
          action={employers ? <span className="text-xs text-muted-foreground">{employers}+ partners</span> : null}
        >
          <div className="py-6 text-center text-xs text-muted-foreground">No recruiter data available.</div>
        </Panel>

        {/* Compliance / accreditation + cohort flow */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel
            eyebrow="Accreditation & compliance"
            title={inst.compliance_framework || "Compliance posture"}
            testid="eng-accreditation"
            action={<PageLink to="/compliance" label="Open audit" />}
          >
            {inst.accreditation && inst.accreditation.length > 0 ? (
              inst.accreditation.map((a) => (
                <div
                  key={a}
                  className="flex items-center gap-2 py-1.5 border-b border-border last:border-0 text-sm"
                >
                  <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>{a}</span>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-xs text-muted-foreground">No accreditation data available.</div>
            )}
          </Panel>

          <Panel
            eyebrow="Programmes"
            title="Enrolment by branch (top 5)"
            testid="eng-enrolment"
          >
            <div className="py-6 text-center text-xs text-muted-foreground">No enrolment data available.</div>
          </Panel>
        </section>

        {/* Vision / Mission (institutional positioning) */}
        {inst.vision && (
          <Panel
            eyebrow="Institutional positioning"
            title="Vision & Mission"
            testid="eng-vision-mission"
          >
            <div className="space-y-4 text-sm">
              <div>
                <div className="label-eyebrow mb-1">Vision</div>
                <p className="text-foreground/85 leading-relaxed italic">
                  &ldquo;{inst.vision}&rdquo;
                </p>
              </div>
              {inst.mission && inst.mission.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-1">Mission</div>
                  <ul className="space-y-1.5">
                    {inst.mission.map((m2, i) => (
                      <li key={i} className="flex gap-2 text-foreground/85 leading-relaxed">
                        <ClipboardCheck className="h-3.5 w-3.5 text-primary mt-1 shrink-0" />
                        <span>{m2}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
